import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dispatchApi } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { AlertTriangle, Calendar, Loader2, RotateCw, Search } from 'lucide-react';
import { workingDaysBetween } from '../utils/workingDays';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';

const FAILURE_LABELS = {
  parent_on_hold:                       'On hold',
  parent_needs_attention:               'Needs attention',
  parent_data_incomplete:               'Data incomplete',
  parent_intake:                        'Pending intake review',
  parent_reship_unlinked:               'Reship not linked',
  parent_blocked_troublesome_customer:  'Customer blocked',
  parent_cancelled:                     'Cancelled',
  parent_completed:                     'Completed',
  parent_delivered_unpaid:              'Delivered unpaid',
  unknown_stage:                        'Unknown stage',
};

function failureLabel(reason) {
  if (!reason) return null;
  return FAILURE_LABELS[reason] || reason.replace(/^parent_/, '').replace(/_/g, ' ');
}

/**
 * Scheduled splits — every pending shipment whose scheduled_date is in the
 * future, plus a banner for shipments that are due today/earlier but stuck
 * behind a non-ready parent stage (DMS-20260508).
 */
export default function ScheduledSplits() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [missedOpen, setMissedOpen] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, summ] = await Promise.all([
        dispatchApi.listScheduledSplits(),
        dispatchApi.missedScheduledSummary().catch(() => ({ data: null })),
      ]);
      setRows(list.data.shipments || []);
      setSummary(summ.data || null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function runRecovery() {
    setRecoverLoading(true);
    try {
      const res = await dispatchApi.recoverMissedScheduled();
      setRecoveryResult(res.data);
      setMissedOpen(true);
      const moved = res.data.moved || 0;
      const blocked = (res.data.blocked || []).length;
      if (moved === 0 && blocked === 0) {
        toast.success('Nothing overdue.');
      } else {
        toast.success(`Recovered ${moved} · ${blocked} blocked`);
      }
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to recover');
    } finally { setRecoverLoading(false); }
  }

  // Per Kalpana 2026-04-29: list orders, not shipments. A multi-item order
  // scheduled for the same day used to render as one row per item. Now we
  // group by (date, order_id) and aggregate.
  const groups = useMemo(() => {
    const byDate = new Map();
    for (const s of rows) {
      const d = String(s.scheduled_date).slice(0, 10);
      if (!byDate.has(d)) byDate.set(d, new Map());
      const byOrder = byDate.get(d);
      const existing = byOrder.get(s.order_id);
      if (existing) {
        existing.quantity += s.quantity || 0;
        existing.shipment_count += 1;
        if (s.vendor_name && existing.vendor_name && s.vendor_name !== existing.vendor_name) {
          existing.vendor_name = 'Mixed';
        } else if (s.vendor_name && !existing.vendor_name) {
          existing.vendor_name = s.vendor_name;
        }
      } else {
        byOrder.set(s.order_id, {
          order_id: s.order_id,
          order_id_external: s.order_id_external,
          customer_name: s.customer_name,
          intake_filename: s.intake_filename,
          is_reship: s.is_reship,
          vendor_name: s.vendor_name || null,
          quantity: s.quantity || 0,
          shipment_count: 1,
          scheduled_date: s.scheduled_date,
          lifecycle_stage: s.lifecycle_stage,
        });
      }
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, byOrder]) => [date, Array.from(byOrder.values())]);
  }, [rows]);

  const overdue = summary?.overdue || 0;
  const eligibleNow = summary?.eligibleNow || 0;
  const blockedBreakdown = summary?.blockedByReason || {};
  const blockedTotal = Object.values(blockedBreakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-1 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Scheduled splits</h1>
          <p className="text-sm text-gray-500 mb-2">
            Shipments scheduled to dispatch on a future date. Cron auto-promotes due shipments
            at 00:00 IST every day; use the recovery button below to force a fresh run.
          </p>
        </div>
        {isAdmin() && (
          <button
            onClick={runRecovery}
            disabled={recoverLoading}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
            title="Re-run the scheduled-promotion sweep now"
          >
            {recoverLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RotateCw className="w-4 h-4" />}
            Recover missed now
          </button>
        )}
      </div>

      {overdue > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                {overdue} shipment{overdue === 1 ? '' : 's'} overdue (scheduled today or earlier)
              </div>
              <div className="text-xs text-amber-800 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span><strong>{eligibleNow}</strong> eligible — should appear on Ready for Dispatch.</span>
                {blockedTotal > 0 && (
                  <span><strong>{blockedTotal}</strong> blocked:
                    {' '}{Object.entries(blockedBreakdown)
                      .map(([k, v]) => `${failureLabel(k)} (${v})`).join(', ')}.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No future-scheduled shipments. Everything pending is ready to ship today or earlier.
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(([date, items]) => (
            <div key={date} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold">{format(new Date(date), 'EEE dd MMM yyyy')}</span>
                <span className="text-xs text-gray-500">{items.length} order(s)</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Order</th>
                    <th className="text-left px-3 py-2">File name</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Vendor</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-right px-3 py-2" title="Working days from today (Sundays excluded)">Defer days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((s) => {
                    const deferDays = workingDaysBetween(new Date(), new Date(s.scheduled_date));
                    return (
                    <tr key={s.order_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/orders/${s.order_id}`} className="text-blue-600 hover:underline">
                          {s.order_id_external}
                        </Link>
                        {s.is_reship && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">reship</span>}
                        {s.shipment_count > 1 && (
                          <span className="ml-1 text-[10px] text-gray-500"
                                title={`${s.shipment_count} items in this order`}>
                            ×{s.shipment_count}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[180px] truncate" title={s.intake_filename || ''}>
                        {s.intake_filename || '—'}
                      </td>
                      <td className="px-3 py-2">{s.customer_name}</td>
                      <td className="px-3 py-2 text-right">{s.quantity}</td>
                      <td className="px-3 py-2 text-xs">{s.vendor_name || '—'}</td>
                      <td className="px-3 py-2 text-xs capitalize">{s.is_reship ? 'reship' : 'standard'}</td>
                      <td className="px-3 py-2 text-right text-xs font-semibold text-gray-700">{deferDays}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={missedOpen}
        onClose={() => setMissedOpen(false)}
        title="Recovery run results"
        size="lg"
      >
        {!recoveryResult ? (
          <p className="text-sm text-gray-600">No data.</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <div className="text-xs uppercase tracking-wide text-green-700">Moved</div>
                <div className="text-xl font-semibold text-green-900">{recoveryResult.moved || 0}</div>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="text-xs uppercase tracking-wide text-amber-700">Blocked</div>
                <div className="text-xl font-semibold text-amber-900">{(recoveryResult.blocked || []).length}</div>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-600">Total checked</div>
                <div className="text-xl font-semibold text-gray-900">{recoveryResult.total || 0}</div>
              </div>
            </div>

            {(recoveryResult.blocked || []).length > 0 && (
              <>
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    These shipments can't be auto-promoted — open each order to resolve the blocker
                    (hold, vendor issue, data incomplete, etc.) and then re-run recovery.
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2">Order</th>
                      <th className="text-left px-3 py-2">Customer</th>
                      <th className="text-left px-3 py-2">Stage</th>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-left px-3 py-2">Scheduled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recoveryResult.blocked.map((s) => (
                      <tr key={s.shipmentId} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link
                            to={`/orders/${s.orderId}`}
                            className="text-blue-600 hover:underline"
                            onClick={() => setMissedOpen(false)}
                          >
                            {s.orderIdExternal}
                          </Link>
                        </td>
                        <td className="px-3 py-2">{s.customerName}</td>
                        <td className="px-3 py-2 text-xs capitalize">{(s.parentStage || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{failureLabel(s.reason)}</td>
                        <td className="px-3 py-2 text-xs">
                          {format(new Date(s.scheduledDate), 'dd MMM yyyy')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
